import { current, produce } from 'immer'

import { extract_author, extract_end, extract_log } from './stream.handler'

import { Message, MessageType } from '../../../common/ai.dictionary'
import { AiState } from '../../ai.state'

/**
 * Per-chunk handler for AiAuditorActions.getAnswerEventStream — incremental
 * rendering for the auditor SSE path (POST /agents/auditor/chat).
 *
 * The backend (agents/graphs/auditor/nodes.py) writes custom-mode chunks with
 * one of these shapes inside `meta`:
 * `{ agent: "Auditor", debug: { step, status, info, timestamp } }`,
 * `{ agent: "Auditor", answer: { greetings: {...} } }`,
 * `{ agent: "Auditor", answer: { check_process: { response, type_of_action, reasoning } } }`,
 * `{ agent: "Auditor", answer: { check_ui: { response, type_of_action, reasoning } } }`,
 * `{ agent: "Auditor", answer: { reasoning: "..." } }`,
 * `{ agent: "Auditor", answer: { operation: "answer", response: "..." } }`,
 * `{ agent: "Auditor", end: { operation: "answer" | "end" | ... } }`.
 *
 * `check_process` / `check_ui` carry the progressive audit text — each chunk
 * holds the full message-so-far (not a delta), so we overwrite the last AI
 * message rather than append.
 */
export const onAuditorAnswerEventStream = (
  state: AiState,
  { response }: { response: any }
): AiState =>
  produce(state, (draft: AiState) => {
    if (!current(draft).backgroundAiResponse) {
      draft.streamingAiResponse = true
      draft.pendingAiResponse = true
    }
    draft.pendingRouteToAuditor = null

    const { message } = response
    if (!message || typeof message !== 'object') {
      return
    }

    // 1) Log frames go into the agent-activity pane, not the chat panel.
    const log = extract_log(message)
    if (log) {
      draft.logs.unshift({
        agent: log.agent,
        message: log.debug.info,
        message_type: 'INFO',
        timestamp: log.timestamp,
        status: log.debug.status,
        step: log.debug.step,
      })
      draft.currentAITaskMessage = log.debug.info
      return
    }

    // 2) End frame — commit the in-flight message, clean up flags.
    const end = extract_end(message)
    if (end) {
      const lastMessage = draft.messages[draft.messages.length - 1]
      if (lastMessage && lastMessage.type === MessageType.AiMsg && !lastMessage.commited) {
        lastMessage.commited = true
      }
      draft.streamingAiResponse = false
      draft.pendingAiResponse = false
      draft.currentAITaskMessage = null
      draft.hasAudit = true
      if (lastMessage?.message) {
        draft.generatedAiRecommendation = lastMessage.message
      }
      return
    }

    // 3) Answer frame — accumulate / overwrite the in-flight AI message.
    const auditorText = extractAuditorText(message)
    const reasoning = extractAuditorReasoning(message)
    if (!auditorText && !reasoning) {
      return
    }

    const author = extract_author(message) || 'Auditor'
    const lastMessageDraft = draft.messages[draft.messages.length - 1]
    const isLastMessageOfTypeAi =
      lastMessageDraft && lastMessageDraft.type === MessageType.AiMsg && !lastMessageDraft.commited

    if (isLastMessageOfTypeAi) {
      if (auditorText) {
        lastMessageDraft.message = formatAuditorMessage(auditorText)
      }
      lastMessageDraft.author = author
      lastMessageDraft.html_preview = null
      lastMessageDraft.type = MessageType.AiMsg
      // Stash the raw reasoning on the message for the panel that surfaces it.
      if (reasoning) {
        (lastMessageDraft as Message & { reasoning?: string }).reasoning = reasoning
      }
    } else if (auditorText) {
      draft.messages.push({
        author,
        message: formatAuditorMessage(auditorText),
        type: MessageType.AiMsg,
        actions: { show: true },
      })
    }
  })

/**
 * Terminal handler for AiAuditorActions.streamCompleted — fires once the
 * SSE EventSource is closed (or the user cancels). Commits the in-flight
 * message and clears streaming flags. Safe to fire after an end frame too.
 */
export const onAuditorStreamCompleted = (state: AiState): AiState =>
  produce(state, (draft: AiState) => {
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.currentAITaskMessage = null
    draft.pendingRouteToAuditor = null
    const lastMessage = draft.messages[draft.messages.length - 1]
    if (lastMessage && lastMessage.type === MessageType.AiMsg && !lastMessage.commited) {
      lastMessage.commited = true
      draft.hasAudit = true
      draft.generatedAiRecommendation = lastMessage.message || ''
    }
  })

/**
 * Begin-streaming handler — fires when AiAuditorActions.checkUIStream or
 * checkProcessStream is dispatched. Seeds an empty AI message that the
 * subsequent getAnswerEventStream chunks fill in. The user's prompt is
 * already pushed by AiActions.typeAreaSubmit, so this handler MUST NOT
 * push another user message (would duplicate the prompt bubble in chat).
 */
export const onAuditorStreamBegin = (state: AiState): AiState =>
  produce(state, (draft: AiState) => {
    draft.streamingAiResponse = true
    draft.pendingAiResponse = true
    draft.hasAudit = false
    draft.generatedAiRecommendation = ''
    // Placeholder AI message — subsequent chunks overwrite `message`.
    draft.messages.push({
      author: 'Auditor',
      message: '',
      type: MessageType.AiMsg,
      actions: { show: true },
    })
  })

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Pull the user-visible audit text out of a streamed meta payload. The
 * auditor backend uses three different keys depending on which graph node
 * produced the chunk — we handle all of them.
 */
function extractAuditorText(messagePayload: any): string {
  try {
    const answer = messagePayload?.answer
    if (!answer) {
      return ''
    }
    if (answer.check_ui?.response) {
      return String(answer.check_ui.response)
    }
    if (answer.check_process?.response) {
      return String(answer.check_process.response)
    }
    if (answer.extract_compliance?.response) {
      return String(answer.extract_compliance.response)
    }
    if (answer.greetings?.message) {
      return String(answer.greetings.message)
    }
    // Final answer frame: { operation: "answer", response: "..." }
    if (answer.operation === 'answer' && typeof answer.response === 'string') {
      return answer.response
    }
    return ''
  } catch (e) {
    console.error('extractAuditorText error', e, messagePayload)
    return ''
  }
}

/**
 * Pull the reasoning chunk if the backend emitted one (it does so on the
 * dedicated `reasoning` node, and inside check_process / check_ui payloads).
 * Returned separately so the UI can surface it in a different pane.
 */
function extractAuditorReasoning(messagePayload: any): string {
  try {
    const answer = messagePayload?.answer
    if (!answer) {
      return ''
    }
    if (typeof answer.reasoning === 'string') {
      return answer.reasoning
    }
    if (answer.check_ui?.reasoning) {
      return String(answer.check_ui.reasoning)
    }
    if (answer.check_process?.reasoning) {
      return String(answer.check_process.reasoning)
    }
    return ''
  } catch (e) {
    console.error('extractAuditorReasoning error', e, messagePayload)
    return ''
  }
}

/**
 * Lightweight markdown-ish formatting for the audit message. The backend
 * tends to emit a bullet list of findings; wrap it for the chat panel.
 */
function formatAuditorMessage(text: string): string {
  if (!text) {
    return ''
  }
  // Don't double-wrap if the chunk already contains HTML (some nodes do).
  if (/<[a-z][^>]*>/i.test(text)) {
    return text
  }
  return text
}
